import type { GetInputPropsOptions } from 'downshift-legacy';
import type { ChangeEventHandler, ComponentType, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Except } from 'type-fest';
import { useDebouncedCallback } from 'use-debounce';
import { InputAutocomplete } from '../InputAutocomplete';
import { AddressItem } from './AddressItem';
import { ManualAddress } from './ManualAddress';
import styles from './styles.module.css';
import { findAddressComponent, loadGoogleMaps, normalizeAddressFromGoogle } from './utils';
import type { FormattedAddress, GoogleMaps } from './utils';

export { type FormattedAddress } from './utils';

type AutocompleteService = google.maps.places.AutocompleteService;
type AutocompletionRequest = google.maps.places.AutocompletionRequest;
type AutocompletePrediction = google.maps.places.AutocompletePrediction;
interface ManualModeItem {
  type: 'manual-mode';
}

type Item = AutocompletePrediction | ManualModeItem;

const addressToString = (item?: Item | null): string | null => {
  if (!item) return null;
  if ((item as ManualModeItem).type === 'manual-mode') return null;
  return (item as AutocompletePrediction).description;
};
const selectedItemChanged = (prevItem: any, item: any): boolean =>
  (!prevItem && item) || (prevItem && !item) || prevItem.id !== item.id;

const getSelectedAddressIndex = (item: Item, itemList: AutocompletePrediction[]): number | null => {
  if ((item as ManualModeItem).type === 'manual-mode') return null;

  const index = itemList.findIndex(
    (itemFromList) => itemFromList.place_id === (item as AutocompletePrediction).place_id,
  );

  return index > -1 ? index + 1 : null;
};

type NormalizedAddressToString = (address: FormattedAddress) => string;

const defaultNormalizedAddressToString: NormalizedAddressToString = (address) => {
  let normalizedInitialValue = '';
  if (address.streetNumber) {
    normalizedInitialValue += `${address.streetNumber} `;
  }
  if (address.streetName) {
    normalizedInitialValue += `${address.streetName}, `;
  }
  if (address.city) {
    normalizedInitialValue += `${address.city}`;
  }
  return normalizedInitialValue;
};

const defaultMinLengthBeforeAutocomplete = 3;
const placesApiDebounceCallWaitTime = 950;

interface InternalInputAddressData {
  predictionsCount: number | null;
  triesCount: number | null;
}

export interface InputAddressData extends InternalInputAddressData {
  selectedAddressIndex: number | null;
}

export interface InputAddressProps extends Omit<GetInputPropsOptions, 'onChange'> {
  initialValue?: FormattedAddress;
  options?: Except<AutocompletionRequest, 'input'>;
  minLengthBeforeAutocomplete?: number;
  emptyComponent: ComponentType<Record<string, never>>;
  normalizedAddressToString?: NormalizedAddressToString;
  onChange: (newValue: null | FormattedAddress, addressData?: InputAddressData) => void;
  onManualModeRequired?: () => void;
}

interface AutocompleteResult {
  inputValue: string;
  predictions: google.maps.places.AutocompletePrediction[] | undefined;
  isLoading: boolean;
}

export function InputAddress({
  initialValue,
  options = {},
  minLengthBeforeAutocomplete = defaultMinLengthBeforeAutocomplete,
  emptyComponent,
  normalizedAddressToString = defaultNormalizedAddressToString,
  onChange,
  onManualModeRequired,
  ...otherProps
}: InputAddressProps): ReactNode {
  const [defaultInputValue] = useState(() => {
    return initialValue ? normalizedAddressToString(initialValue) : '';
  });
  const [inputValue, setInputValue] = useState(defaultInputValue);

  const addressDataRef = useRef<InternalInputAddressData>({
    predictionsCount: null,
    triesCount: null,
  });

  const mapsRef = useRef<GoogleMaps | undefined>(undefined);
  const autocompleteServiceRef = useRef<AutocompleteService | undefined>(undefined);

  const [autocompleteResult, setAutocompleteResult] = useState<AutocompleteResult>({
    inputValue: '',
    predictions: undefined,
    isLoading: false,
  });

  const autocompleteResultRef = useRef(autocompleteResult);
  autocompleteResultRef.current = autocompleteResult;

  const getAndSetAddress = useDebouncedCallback(
    (currentInputValue: string, predictionRequest: AutocompletionRequest) => {
      addressDataRef.current = {
        ...addressDataRef.current,
        predictionsCount: null,
      };
      setAutocompleteResult({
        inputValue: currentInputValue,
        predictions: undefined,
        isLoading: true,
      });
      autocompleteServiceRef.current?.getPlacePredictions(predictionRequest, (predictions, status) => {
        if (autocompleteResultRef.current.inputValue !== currentInputValue) {
          return;
        }

        addressDataRef.current = {
          ...addressDataRef.current,
          predictionsCount: (predictions || []).length,
          triesCount: (addressDataRef.current.triesCount || 0) + 1,
        };

        setAutocompleteResult({
          inputValue: currentInputValue,
          predictions: status === mapsRef.current?.places.PlacesServiceStatus.OK ? predictions : [],
          isLoading: false,
        });
      });
    },
    placesApiDebounceCallWaitTime,
  );

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.target;

    setInputValue(value);

    if (value !== defaultInputValue) {
      onChange(null);
    }

    if (!value || (minLengthBeforeAutocomplete && value.length < minLengthBeforeAutocomplete)) {
      setAutocompleteResult({
        inputValue: value,
        predictions: undefined,
        isLoading: false,
      });
      return;
    }

    if (!autocompleteServiceRef.current) {
      return;
    }

    const predictionRequest = { input: value, ...options };
    getAndSetAddress(value, predictionRequest);
  };
  const handleInputChangeRef = useRef(handleInputChange);
  handleInputChangeRef.current = handleInputChange;

  useEffect(() => {
    loadGoogleMaps().then((maps) => {
      mapsRef.current = maps;
      autocompleteServiceRef.current = new maps.places.AutocompleteService();
      // Triggers the search when an initial value is given
      if (defaultInputValue) {
        handleInputChangeRef.current({
          target: {
            value: defaultInputValue,
          },
        } as any);
      }
    });
  }, [defaultInputValue]);

  const handleAddressSelected = (item: Item): void => {
    const maps = mapsRef.current;
    if (!maps) return;

    if (onManualModeRequired && item && (item as ManualModeItem).type === 'manual-mode') {
      onManualModeRequired();
      return;
    }

    if (!onChange) return;

    if (!item) {
      onChange(null);
      return;
    }

    const selectedAddressIndex = autocompleteResult.predictions
      ? getSelectedAddressIndex(item, autocompleteResult.predictions)
      : null;

    const address = item as AutocompletePrediction;
    const service = new maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        fields: ['address_components', 'geometry.location'],
        placeId: address.place_id,
      },
      (place, status) => {
        if (status === maps.places.PlacesServiceStatus.OK && place.address_components) {
          const normalizedAddress = normalizeAddressFromGoogle(place, address.description);
          if (normalizedAddress.zipcode) {
            onChange(normalizedAddress, { ...addressDataRef.current, selectedAddressIndex });
            return;
          }

          // If we don't have a zipcode from the place request service, we try a second geocode
          const country = findAddressComponent('country', place.address_components);
          const geocoder = new maps.Geocoder();
          geocoder.geocode({ address: `${normalizedAddress.city} ${country || ''}` }, (results) => {
            onChange(
              {
                ...normalizedAddress,
                zipcode:
                  results.length > 0 && results[0].address_components
                    ? findAddressComponent('postal_code', results[0].address_components)
                    : normalizedAddress.zipcode,
              },
              { ...addressDataRef.current, selectedAddressIndex },
            );
          });
        }
      },
    );
  };

  const isLoading = autocompleteResult.inputValue !== inputValue || autocompleteResult.isLoading;

  return (
    <InputAutocomplete
      defaultInputValue={defaultInputValue}
      inputProps={{
        ...otherProps,
        onChange: handleInputChange,
        icon: isLoading ? 'loading' : undefined,
      }}
      minLengthBeforeAutocomplete={minLengthBeforeAutocomplete}
      emptyComponent={emptyComponent}
      itemToString={addressToString}
      selectedItemChanged={selectedItemChanged}
      isLoading={isLoading}
      onChange={handleAddressSelected}
    >
      {autocompleteResult.inputValue !== inputValue
        ? []
        : autocompleteResult.predictions && [
            ...autocompleteResult.predictions.map((item) => (
              <InputAutocomplete.Item key={item.place_id} item={item}>
                <AddressItem address={item} />
              </InputAutocomplete.Item>
            )),
            ...(onManualModeRequired
              ? [
                  <InputAutocomplete.Item
                    key="manual-mode"
                    item={{ type: 'manual-mode' }}
                    className={styles.ManualAddressLiOverride}
                    classes={{
                      selected: styles.ManualAddressLiOverrideSelected,
                      highlighted: styles.ManualAddressLiOverrideHighlighted,
                    }}
                  >
                    <ManualAddress />
                  </InputAutocomplete.Item>,
                ]
              : []),
          ]}
    </InputAutocomplete>
  );
}
